/**
 * Tracking → Delivery's tile strip, and the sections each tile opens.
 *
 * Theresa, Oct 19 2026: *"I want tiles at the top to reference different parts of the hiring KPIs
 * and then (collapsed) sections at the bottom that they are linked to. Once someone clicks a tile
 * at the top the more detailed data will be shown on the bottom."*
 *
 * So a tile is a whole section compressed to one figure, and clicking it opens that section and
 * scrolls to it. The tile is the question; the section is the evidence.
 *
 * ⚠️ ONE OPEN SECTION AT A TIME, and the open tile is marked. Several open at once turns the
 * bottom of the tab back into the stack of sections this replaced, and the link between a tile and
 * its detail stops being visible the moment two are showing.
 *
 * ⚠️ THE SECTIONS STAY MOUNTED when shut (`hidden`, not unmounted). The candidate funnel inside
 * Prospects owns a fetch; unmounting it would re-read the warehouse every time somebody closed and
 * reopened a tile, and the offer tile's four-week series would flicker back to its loading state.
 *
 * ⚠️ Clicking the OPEN tile shuts it. A control that can only ever open is a control that leaves
 * the reader hunting for the close.
 */
import { useCallback, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { cn } from '../../../../../shared';
import { WhatThisCounts } from './trackingUi';

export type TileId = 'plan' | 'starts' | 'prospects' | 'trends' | 'clusters';

export interface TileSpec {
  id: TileId;
  label: string;
  value: string;
  note?: string;
  tone?: 'neutral' | 'ok' | 'warn';
  why: string;
}

const TONE: Record<'neutral' | 'ok' | 'warn', string> = {
  neutral: 'text-gray-900',
  ok: 'text-emerald-700',
  warn: 'text-amber-700',
};

export function useTileSections() {
  const [open, setOpen] = useState<TileId | null>(null);
  const refs = useRef(new Map<TileId, HTMLDivElement | null>());

  const toggle = useCallback((id: TileId) => {
    setOpen((cur) => {
      const next = cur === id ? null : id;
      if (next) {
        /*
         * After paint, so the section has its height before we scroll to it. `block: 'start'` on a
         * section that was `hidden` a frame ago otherwise lands on where it used to be.
         */
        requestAnimationFrame(() =>
          refs.current.get(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        );
      }
      return next;
    });
  }, []);

  const register = useCallback(
    (id: TileId) => (el: HTMLDivElement | null) => {
      refs.current.set(id, el);
    },
    []
  );

  return { open, toggle, register };
}

export function TileStrip({
  tiles,
  open,
  onToggle,
}: {
  tiles: TileSpec[];
  open: TileId | null;
  onToggle: (id: TileId) => void;
}) {
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
      {tiles.map((t) => (
        <button
          key={t.id}
          type="button"
          data-testid={`tile-${t.id}`}
          onClick={() => onToggle(t.id)}
          aria-expanded={open === t.id}
          aria-controls={`section-${t.id}`}
          className={cn(
            'rounded-xl border bg-white px-4 py-3 text-left transition-colors',
            open === t.id
              ? 'border-blue-500 ring-1 ring-blue-500'
              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
          )}
        >
          <div className="flex items-center gap-1 text-xs font-medium text-gray-500">
            {t.label}
            <WhatThisCounts label={t.label} why={t.why} />
          </div>
          <div className={cn('mt-0.5 text-2xl font-semibold tabular-nums', TONE[t.tone ?? 'neutral'])}>
            {t.value}
          </div>
          {t.note ? <div className="text-xs leading-4 text-gray-500">{t.note}</div> : null}
          <div className="mt-1.5 text-[11px] font-medium text-blue-600">
            {open === t.id ? 'Hide the detail' : 'Show the detail'}
          </div>
        </button>
      ))}
    </div>
  );
}

/**
 * One tile's detail. Kept in the DOM when shut — see the note at the top of this file.
 *
 * ⚠️ IT CARRIES NO HEADING OF ITS OWN. Three of the five sections are existing components that
 * already render one (`Section` from `trackingUi`), so a wrapper heading put "Progress by cluster"
 * on the screen twice — and the duplicate is not only ugly, it makes every heading query on this
 * tab ambiguous. Each section owns exactly one heading; the two that had none were given one.
 */
export function TileSection({
  id,
  open,
  register,
  children,
}: {
  id: TileId;
  open: boolean;
  register: (el: HTMLDivElement | null) => void;
  children: ReactNode;
}) {
  return (
    <div ref={register} id={`section-${id}`} hidden={!open} data-testid={`section-${id}`}>
      {children}
    </div>
  );
}
